/**
 * Authentication Settings Module
 *
 * This module provides functionality for retrieving and managing authentication
 * settings from cookies and development configuration files. It handles:
 *
 * - Retrieving Cognito settings from cookies in production environments
 * - Loading development configuration for local development
 * - Configuring AWS Amplify with the appropriate authentication settings
 * - Polling for settings availability with timeout handling
 */

import getCookieValue from '@utils/cookies';
import { has } from 'lodash';
import { AuthSettingsResult, CognitoSettings } from 'types/Auth.types';

// Configuration file name for development environments
const DEV_CONFIG_FILE_NAME = 'dev-auriga-config.ts';

/**
 * Retrieves Cognito authentication settings from cookies
 *
 * In production environments, these settings are injected by CloudFront functions.
 * The function parses the CognitoParams cookie which contains authentication
 * configuration including user pool IDs, identity provider name, and redirect URLs.
 *
 * @returns The parsed Cognito settings object
 * @throws Error if the cookie is not found or cannot be parsed
 */
export const getCognitoSettings = (): CognitoSettings => {
  // Get the cognito-settings cookie
  const cognitoCookieValue = getCookieValue('CognitoParams');

  if (!cognitoCookieValue) {
    // No cookie found - this is an error in all stages
    throw new Error('Cognito settings cookie not found. Make sure the Cognito config cookie is being set correctly.');
  }

  try {
    // Parse the cookie value
    return JSON.parse(cognitoCookieValue);
  } catch (error) {
    console.error('Failed to parse Cognito settings cookie:', error);
    throw error;
  }
};

/**
 * Checks for authentication configuration settings availability
 *
 * - Checks for development configuration in development environments
 * - Polls for cookie availability with a configurable timeout
 * - Provides detailed error messages and solutions for configuration issues
 *
 * The polling mechanism is necessary because cookies may not be immediately
 * available, especially in development environments where they're set by
 * middleware or in production where they're set by CloudFront functions.
 *
 * @param timeout - Maximum time in milliseconds to wait for settings (default: 5000ms)
 * @returns Promise resolving to an object with settings status and configuration
 */
export const checkAuthConfigSettings = async (timeout = 5000): Promise<AuthSettingsResult> => {
  // Extended checks specific to development environment
  if (process.env.NODE_ENV === 'development') {
    // Check if dev-auriga-config.ts exists and can be imported form the root directory
    try {
      const { default: devAurigaConfig } = await import('../../../dev-auriga-config');
      if (!has(devAurigaConfig, 'CognitoParams')) {
        throw new Error('CognitoParams key is missing from developer config.');
      }
    } catch {
      return {
        isReady: false,
        error: `Missing root ${DEV_CONFIG_FILE_NAME} file`,
        solution: `
  Please create ${DEV_CONFIG_FILE_NAME} that includes your CognitoParams key for development authentication settings.

  Example content:
  module.exports = {
    CognitoParams: {
      region: 'us-west-2',
      userPoolId: 'us-west-2_example',
      userPoolWebClientId: 'your-client-id',
      identityPoolId: 'us-west-2:example',
      domainName: 'your-domain',
      redirectURL: 'http://localhost:3000',
      identityProvider: 'alpha-aurigaFrontend-IdProvider'
    },
    "auriga-settings": {
      region: 'us-west-2',
      authenticationType: 'apiKey',
      apiParams: {
        deckManagementGraphqlEndpoint: 'deckManagement-graphql-endpoint',
        dimSumApiKey: 'dimSum-api-key',
        dimSumGraphqlEndpoint: 'dimSum-graphql-endpoint',
        fpaOneApiKey: 'fpaOne-api-key',
        fpaOneGraphqlEndpoint: 'fpaOne-graphql-endpoint
      }
    }
  };

  Contact a teammate if you need the actual values.`,
      };
    }
  }

  // Wait for the cookie to be set with polling mechanism
  return new Promise((resolve) => {
    const startTime = Date.now();

    const checkCookie = (startCount: number) => {
      let attemptCount = startCount;
      attemptCount += 1;

      try {
        const settings = getCognitoSettings();
        resolve({ isReady: true, settings });
      } catch {
        const elapsed = Date.now() - startTime;
        if (elapsed > timeout) {
          console.error(`Cookie check timed out after ${timeout}ms, and ${attemptCount} attempts.`);
          resolve({
            isReady: false,
            error: 'Authentication settings not available',
            solution:
              process.env.NODE_ENV === 'development'
                ? 'Make sure your dev server is running and root dev-auth-config.ts file is properly configured.'
                : 'Authentication cookie not getting set from CloudFormation. Please contact support if this persists.',
          });
        } else {
          // Poll frequently until cookie is found (every 100ms)
          console.debug(`Cookie not found yet. Elapsed: ${elapsed}ms. Trying again at attempt: ${attemptCount}...`);
          setTimeout(() => checkCookie(attemptCount), 100);
        }
      }
    };
    checkCookie(0);
  });
};

/**
 * Configures AWS Amplify with authentication settings
 * - Retrieves Cognito settings from cookies
 * - Creates an Amplify v6+ compatible configuration object
 * - Sets up OAuth configuration for federated authentication
 *
 * The configuration follows AWS Amplify v6+ structure which separates
 * authentication providers into distinct sections.
 *
 * @returns The complete Amplify configuration object
 */
export const configureAmplify = async (): Promise<Record<string, unknown>> => {
  const settings = getCognitoSettings();

  return {
    Auth: {
      Cognito: {
        region: settings.region,
        userPoolId: settings.userPoolId,
        userPoolClientId: settings.userPoolWebClientId,
        identityPoolId: settings.identityPoolId,
        loginWith: {
          oauth: {
            domain: `${settings.domainName}.auth.${settings.region}.amazoncognito.com`,
            scopes: ['email', 'openid', 'profile', 'aws.cognito.signin.user.admin'],
            redirectSignIn: [settings.redirectURL],
            redirectSignOut: [settings.redirectURL],
            responseType: 'code',
          },
        },
      },
    },
    API: {
      GraphQL: {
        endpoint: 'placeholder', // Will be overridden by generateClient calls in src/services/api/index.ts. Placeholder prevents console warnings
        region: settings.region,
        defaultAuthMode: 'iam',
      },
    },
  };
};
